// What? A React component for managing selection options for Selection-type data fields.
// Why? To allow users to define the list of options that will be available in dropdown fields.
// How? Provides add/remove functionality for options with validation to ensure all options have values.

import React, { useCallback } from "react";
import { Box, Button, Input, Stack, Typography } from "@mui/joy";
import { useValidation } from "../../../contexts/ValidationContext";

interface FieldOptionsControlsProps {
  options: string[];
  onChange: (options: string[]) => void;
  fieldId: string;
}

const FieldOptionsControls: React.FC<FieldOptionsControlsProps> = ({
  options,
  onChange,
  fieldId,
}) => {
  const { showValidation } = useValidation();

  const addOption = useCallback(() => {
    const newOptions = [...options, ""];
    onChange(newOptions);
  }, [options, onChange]);

  const updateOption = useCallback(
    (index: number, value: string) => {
      const newOptions = [...options];
      newOptions[index] = value;
      onChange(newOptions);
    },
    [options, onChange],
  );

  const removeOption = useCallback(
    (index: number) => {
      const newOptions = options.filter((_, i) => i !== index);
      onChange(newOptions);
    },
    [options, onChange],
  );

  // Validation logic
  const emptyOptions = options.filter((opt) => !opt.trim());
  const nonEmptyOptions = options.filter((opt) => opt.trim());
  let error: string | undefined = undefined;

  if (showValidation) {
    if (nonEmptyOptions.length === 0) {
      error = "At least one non-empty option is required";
    } else if (emptyOptions.length > 0) {
      error = "All options must have a value";
    }
  }

  return (
    <Stack spacing={2}>
      <Typography level="title-sm">Select Options</Typography>

      <Stack spacing={1}>
        {options.map((option, index) => (
          <Stack
            key={`option-${index}`}
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <Input
              value={option}
              onChange={(e) => updateOption(index, e.target.value)}
              placeholder="Enter option value"
              error={showValidation && !option.trim()}
              sx={{ flex: 1 }}
              size="sm"
            />
            <Button
              variant="outlined"
              color="neutral"
              size="sm"
              onClick={() => removeOption(index)}
            >
              Remove
            </Button>
          </Stack>
        ))}
      </Stack>

      <Box>
        <Button
          variant="outlined"
          color="primary"
          size="sm"
          onClick={addOption}
        >
          + Add Option
        </Button>
      </Box>

      {error && (
        <Typography level="body-sm" color="danger">
          {error}
        </Typography>
      )}
    </Stack>
  );
};

export default FieldOptionsControls;
